import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Tenant from '../types/tenant';
import { TenantContext, TenantContextProviderProps, useWebSocketContext } from '.';
import { IUser } from '../types/user';
import { useIssuerService, useVerificationService } from '../services';
import { IVerification } from '../types/verification';
/**
 * TenantContextProvider
 *
 * @param {*} { children }
 * @return {*}
 */
const TenantContextProvider: React.FC<TenantContextProviderProps> = ({ children }) => {
  const verificationService = useVerificationService();
  const issuerService = useIssuerService();

  const { lastMessage } = useWebSocketContext();

  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isLoadingCredentials, setIsLoadingCredentials] = useState(false);
  const [isRefreshingCredentials, setIsRefreshingCredentials] = useState(false);

  /*
   * State Variables
   */
  const [tenantData, setTenantData] = useState<Tenant | undefined>(undefined);
  const [userData, setUserData] = useState<IUser[] | undefined>(undefined);
  const [schemaData, setSchemaData] = useState<any[] | undefined>(undefined);
  const [credentialsData, setCredentialsData] = useState<any[] | undefined>(undefined);
  const [verificationData, setVerificationData] = useState<IVerification[] | undefined>(undefined);
  const [verificationRequestsData, setVerificationRequestsData] = useState<any[] | undefined>(
    undefined
  );
  /*
   * Memoize the values
   */
  const tenant = useMemo(() => tenantData, [tenantData]);
  const users = useMemo(() => userData, [userData]);
  const schemas = useMemo(() => schemaData, [schemaData]);
  const credentials = useMemo(() => credentialsData, [credentialsData]);
  const verifications = useMemo(() => verificationData, [verificationData]);
  const verificationRequests = useMemo(() => verificationRequestsData, [verificationRequestsData]);
  /*
   * Tenant Context Functions
   */
  const fetchTenant = useCallback(async () => {
    const response = await issuerService.fetchTenantData();
    if (response) setTenantData(response);
    return response;
  }, [issuerService]);

  /*
   * User Context Functions
   */
  const fetchUsers = useCallback(async () => {
    const response = await issuerService.fetchUserData();
    if (response) setUserData(response);
    return response;
  }, [issuerService]);

  /*
   * Verification Context Functions
   */
  const fetchVerifications = useCallback(async () => {
    const response = await verificationService.fetchVerifications();
    if (response) setVerificationData(response);
    return response;
  }, [verificationService]);

  /*
   * Verification Requests Context Functions
   */
  const fetchVerificationRequests = useCallback(async () => {
    const response = await verificationService.fetchVerificationRequests();
    if (response) setVerificationRequestsData(response);
    return response;
  }, [verificationService]);

  /*
   * Credential Context Functions
   */
  const fetchCredentials = useCallback(
    async (connection_id: string) => {
      setIsLoadingCredentials(true);
      const credentials = await issuerService.fetchCredentialData(connection_id);
      if (credentials) setCredentialsData(credentials);
      setIsLoadingCredentials(false);
      return credentials;
    },
    [issuerService]
  );

  /*
   * Refresh Credentials Context Functions
   */
  const refreshCredentials = useCallback(
    async (connection_id: string) => {
      setIsRefreshingCredentials(true);
      const credentials = await issuerService.fetchCredentialData(connection_id);
      if (credentials) setCredentialsData(credentials);
      setIsRefreshingCredentials(false);
    },
    [issuerService]
  );

  /*
   * Schema Context Functions
   */
  const fetchSchemas = useCallback(async () => {
    const response = await issuerService.fetchSchemaData();
    if (response) setSchemaData(response);
    return response;
  }, [issuerService]);

  const fetchAllItems = useCallback(async () => {
    const response = await verificationService.fetchAllItems();
    if (response) {
      setVerificationRequestsData(response.requests);
      setUserData(response.users);
      setSchemaData(response.schemas);
      setVerificationData(response.verifications);
    }
    return response;
  }, [verificationService]);
  /*
   * Initialize Context Data
   */
  const initialize = useCallback(async () => {
    if (!isLoading) {
      setIsLoading(true);
      await fetchAllItems();
      setIsLoading(false);
    }
  }, [fetchAllItems, isLoading]);

  /*
   * Refresh Context Functions
   */
  const refresh = useCallback(async () => {
    setIsRefreshing(true);
    await fetchAllItems();
    setIsRefreshing(false);
  }, [fetchAllItems]);

  useEffect(() => {
    const fetchData = async () => {
      await initialize();
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /*
   * Refresh Context Functions
   */
  useEffect(() => {
    const fetchUserData = async () => {
      await fetchUsers();
      await fetchVerifications();
    };
    const fetchVerificationRequestsData = async () => {
      await fetchVerificationRequests();
    };
    if (lastMessage?.data === 'refresh-users') {
      fetchUserData();
    }
    if (lastMessage?.data === 'refresh-verifications') {
      fetchVerificationRequestsData();
    }
  }, [lastMessage, fetchUsers, fetchVerificationRequests]);

  return (
    <TenantContext.Provider
      value={{
        tenant,
        users,
        schemas,
        credentials,
        verifications,
        verificationRequests,
        fetchTenant,
        fetchUsers,
        fetchCredentials,
        refreshCredentials,
        fetchSchemas,
        fetchVerifications,
        fetchVerificationRequests,
        refresh,
        isLoading,
        isRefreshing,
        isLoadingCredentials,
        isRefreshingCredentials
      }}>
      {children}
    </TenantContext.Provider>
  );
};

export default TenantContextProvider;
